import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogTrigger,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import {
  UserPlus, Copy, Check, Trash, XCircle, CheckCircle,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OperatorManagement = () => {
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', display_name: '' });
  const [createdInvite, setCreatedInvite] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/superadmin/operators`, { withCredentials: true });
      setOperators(data);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(
        `${API_URL}/api/superadmin/operators`,
        { ...form, role: 'operator' },
        { withCredentials: true }
      );
      setCreatedInvite(data);
      setForm({ email: '', name: '', display_name: '' });
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create operator');
    }
  };

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { /* clipboard denied */ }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Deactivate this operator? They will be logged out within 15 minutes.')) return;
    try {
      await axios.patch(`${API_URL}/api/superadmin/operators/${id}/deactivate`, {}, { withCredentials: true });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to deactivate');
    }
  };

  const handleActivate = async (id) => {
    try {
      await axios.patch(`${API_URL}/api/superadmin/operators/${id}/activate`, {}, { withCredentials: true });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to activate');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this operator? Their assignments will be removed.')) return;
    try {
      await axios.delete(`${API_URL}/api/superadmin/operators/${id}`, { withCredentials: true });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to delete');
    }
  };

  if (loading) return <div className="text-center py-8">Loading operators...</div>;

  return (
    <div className="space-y-4" data-testid="operator-management">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-heading font-semibold">Operators</h2>
          {/* This used to mention only the assignment scope, which reads as
              though assignment is the whole boundary. It is not, and someone
              deciding from this screen was deciding on half the facts. */}
          <p className="text-sm text-muted-foreground mt-1">
            {operators.length} operator{operators.length === 1 ? '' : 's'}. An operator is staff:
            they reach this admin panel, may issue invitations, and can remove posts or
            silence members in the Commons. Only <strong className="text-foreground">support
            threads</strong> are scoped &mdash; they see the members you assign, and no others.
          </p>
        </div>

        <Dialog
          open={showCreate || !!createdInvite}
          onOpenChange={(open) => {
            if (!open) { setShowCreate(false); setCreatedInvite(null); }
            else setShowCreate(true);
          }}
        >
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90" data-testid="create-operator-button">
              <UserPlus className="w-4 h-4 mr-2" />
              Create Operator
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {createdInvite ? 'Operator Invitation Created' : 'Create Operator'}
              </DialogTitle>
              <DialogDescription>
                {createdInvite
                  ? 'Copy this one-time link now. It expires in 48 hours and cannot be shown again.'
                  : 'The operator will set their own password via a single-use link.'}
              </DialogDescription>
            </DialogHeader>

            {!createdInvite && (
              <form onSubmit={handleCreate} className="space-y-4 pt-2">
                <div className="space-y-2">
                  <Label htmlFor="op-email">Email *</Label>
                  <Input
                    id="op-email" type="email" required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="operator-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="op-name">Name *</Label>
                  <Input
                    id="op-name" required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="operator-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="op-display">Display Name</Label>
                  <Input
                    id="op-display"
                    value={form.display_name}
                    onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                    data-testid="operator-display-name-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary/90"
                  data-testid="submit-operator"
                >
                  Generate Invite Link
                </Button>
              </form>
            )}

            {createdInvite && (
              <div className="space-y-4 pt-2" data-testid="operator-invite-display">
                <div className="p-3 bg-muted border border-border rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">One-time link (copy now):</p>
                  <p className="text-xs font-mono break-all text-foreground" data-testid="operator-invite-url">
                    {createdInvite.onboarding_url}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => copyLink(createdInvite.onboarding_url)}
                    className="flex-1 bg-primary hover:bg-primary/90"
                    data-testid="copy-operator-invite"
                  >
                    {copied ? (
                      <><Check className="w-4 h-4 mr-2" weight="bold" /> Copied</>
                    ) : (
                      <><Copy className="w-4 h-4 mr-2" /> Copy Link</>
                    )}
                  </Button>
                  <Button variant="outline" onClick={() => { setCreatedInvite(null); setShowCreate(false); }}>
                    Done
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {operators.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                  No operators yet. Create one to delegate support.
                </TableCell>
              </TableRow>
            )}
            {operators.map((op) => (
              <TableRow key={op.id} data-testid={`operator-row-${op.id}`}>
                <TableCell className="font-mono text-sm">{op.email}</TableCell>
                <TableCell>{op.display_name || op.name}</TableCell>
                <TableCell>
                  {op.is_active ? (
                    <Badge variant="outline" className="border-green-500/20 bg-green-500/10 text-green-400">
                      <CheckCircle className="w-3 h-3 mr-1" weight="fill" /> Active
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="border-destructive/20 bg-destructive/10 text-destructive">
                      <XCircle className="w-3 h-3 mr-1" weight="fill" /> Pending / Inactive
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    {op.is_active ? (
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => handleDeactivate(op.id)}
                        data-testid={`deactivate-operator-${op.id}`}
                      >
                        Deactivate
                      </Button>
                    ) : (
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => handleActivate(op.id)}
                        data-testid={`activate-operator-${op.id}`}
                      >
                        Reactivate
                      </Button>
                    )}
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => handleDelete(op.id)}
                      className="text-destructive hover:text-destructive"
                      data-testid={`delete-operator-${op.id}`}
                    >
                      <Trash className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default OperatorManagement;
